import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MapComponent from "../../common/map";
import axios from "axios";
import { motion } from "framer-motion";

const Campus = ({ showHeader = true }) => {
  const [allCampusessAddresses, setAllCampusessAddresses] = useState([]);
  const [campusData, setCampusData] = useState([]);
  const [showAllCampuses, setShowAllCampuses] = useState(!showHeader); // Show all campuses by default on contact page

  useEffect(() => {
    const getCampusesDetails = () => {
      const query = `page=1&limit=1000`;
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/campus/?${query}`)
        .then((response) => {
          const campuses = response?.data?.campuses;
          // Sort campuses in ascending order by name
          const sortedCampuses = campuses.sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setCampusData(sortedCampuses);
          const allAddresses = sortedCampuses.map((campus) => {
            return campus.address;
          });
          setAllCampusessAddresses(allAddresses);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    };
    getCampusesDetails();
  }, []);

  // Show first 8 campuses, or all if showAllCampuses is true
  const displayedCampuses = showAllCampuses
    ? campusData
    : campusData.slice(0, 8);
  const hasMoreCampuses = campusData.length > 8;

  return (
    <>
      <div className="bg-gradient-to-br from-white to-gray-50 py-16 lg:py-20">
        <div className="w-[90%] mx-auto">
          {/* Header Section - Only show if showHeader is true */}
          {showHeader && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col lg:flex-row lg:justify-between items-center lg:items-end gap-6 lg:gap-0 mb-12"
            >
              <div className="flex flex-col items-center w-full">
                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight font-extrabold text-[#183864] mb-4">
                  Our Campuses
                </h1>
                <p className="text-lg lg:text-xl text-[#5E6282] max-w-2xl text-center">
                  Discover our training locations across the region, each
                  equipped with modern facilities and expert instructors
                </p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="shrink-0 mt-5"
                >
                  <Link
                    to={`/contact`}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#B02526] to-[#8B1E1F] hover:from-[#8B1E1F] hover:to-[#B02526] text-white px-6 py-4 rounded-xl font-semibold text-sm lg:text-base hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    See All Campuses
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Header for contact page */}
          {!showHeader && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl leading-tight font-extrabold text-[#183864] mb-4">
                Our Campus Locations
              </h2>
              <p className="text-lg lg:text-xl text-[#5E6282] max-w-3xl mx-auto">
                Find a campus near you. All our locations are listed in
                alphabetical order for your convenience.
              </p>
            </motion.div>
          )}

          {/* Campus Cards - 4 per row */}
          {campusData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {displayedCampuses.map((campus, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 bg-[#B02526]/10 rounded-full flex items-center justify-center shrink-0">
                          <svg
                            className="w-6 h-6 text-[#B02526]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#14183E] mb-2">
                            {campus.name || `Campus ${index + 1}`}
                          </h4>
                        </div>
                      </div>

                      <div className="flex-1">
                        <p className="text-[#5E6282] text-sm mb-4 line-clamp-3">
                          {campus.address}
                        </p>

                        <div className="flex items-center gap-2 text-sm text-[#B02526] mb-4">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <span className="text-xs">
                            {campus.phone || "Contact for details"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-auto">
                        <Link
                          to={`/contact/${campus._id}`}
                          className="inline-flex items-center gap-2 text-[#B02526] text-sm font-semibold hover:text-[#8B1E1F] transition-colors duration-300"
                        >
                          View Details
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Show More Button - Only show on homepage */}
              {showHeader && hasMoreCampuses && !showAllCampuses && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="text-center mt-8"
                >
                  <button
                    onClick={() => setShowAllCampuses(true)}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#B02526] to-[#d63031] text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Show More Campuses
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </motion.div>
              )}

              {/* Show Less Button - Only show on homepage */}
              {showHeader && showAllCampuses && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="text-center mt-8"
                >
                  <button
                    onClick={() => setShowAllCampuses(false)}
                    className="inline-flex items-center gap-2 bg-gray-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Show Less
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-2 shadow-2xl border border-gray-100">
              <div className="relative overflow-hidden rounded-2xl">
                <MapComponent
                  height="500px"
                  addresses={allCampusessAddresses}
                />

                {/* Map Overlay Info */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg max-w-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 bg-[#B02526] rounded-full animate-pulse"></div>
                    <span className="font-semibold text-[#14183E]">
                      Live Campus Locations
                    </span>
                  </div>
                  <p className="text-sm text-[#5E6282]">
                    Click on any marker to view campus details and contact
                    information
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Campus;
